import React, {MouseEvent} from 'react'
import _ from 'lodash'
import {Panel, P, H5, Button, SortableList, ListItem, Icon, IconButton, Colors, H3, Card, Info, Popup} from '@components/UI/index.js'
import ReasonsSettings from '@components/orders/ReasonsSettings.js'
import {formSpacing, FormRow, FormStack, Input, Toggle, Select} from '@components/UI/forms/index.js'
import {showNotification} from 'src/socketio/socketio.js'
import {Reseller} from 'types/types.js'
import guid from '@utils/guid'
import t from 'src/helpers/Languages'
import OwnOptionModal from './OwnOptionModal'
import dbCodes from 'server/dbCodes'
import type {PopupRef} from '../../../../components/UI/overlays/Popup.js'

type OwnOption = Reseller['settings']['orders']['customOptions'][number]

type OrderSettingsProps = {
    reseller: Reseller
    onChange: (reseller: Reseller, key?: string) => void
}

type OrderSettingsState = {
    selectedOwnOption: OwnOption | null
    selectedOwnColliType: Reseller['settings']['orders']['colliTypes'][number] | null
}

export default class OrderSettings extends React.Component<OrderSettingsProps, OrderSettingsState> {
    popupRef: PopupRef

    constructor(props: OrderSettingsProps) {
        super(props)

        this.state = {
            selectedOwnOption: null,
            selectedOwnColliType: null
        }
    }

    onChangeValue(key: string, parser, event) {
        if (!event && parser) {
            event = parser
            parser = null
        }
        const reseller = {...this.props.reseller}

        if (parser) {
            reseller.settings[key] = parser(event.target.value)
        } else {
            reseller.settings[key] = event.target.value
        }

        this.props.onChange(reseller)
    }

    onSaveValue(key: string, parser, event) {
        if (!event && parser) {
            event = parser
            parser = null
        }

        const {reseller, onChange} = this.props

        if (event && event.target) {
            if (parser) {
                _.set(reseller.settings, key, parser(event.target.value))
            } else {
                _.set(reseller.settings, key, event.target.value)
            }
        }

        onChange(reseller, key)
    }

    onChangeChecked(key: string, event) {
        const reseller = {...this.props.reseller}
        reseller.settings[key] = event.target.checked
        this.props.onChange(reseller, key)
    }

    addCustomOptions() {
        const newOption: Reseller['settings']['orders']['customOptions'][number] = {
            color: Colors.routeColors[Math.floor(Math.random() * Colors.routeColors.length)],
            name: '',
            id: guid.generate()
        }

        this.setState({selectedOwnOption: newOption})
    }

    async removeOwnOption(event: MouseEvent, option: Reseller['settings']['orders']['customOptions'][number]) {
        event.stopPropagation()

        if (!await this.popupRef.open('Bevestig verwijderen', `Weet je zeker dat je eigen optie "${option.name}" wilt verwijderen?`)) return
        this.popupRef.close()

        const {reseller} = this.props
        reseller.settings.orders.customOptions = reseller.settings.orders.customOptions?.filter((o) => o.id !== option.id)

        this.props.onChange(reseller, 'orders.customOptions')
    }

    addColliTypes() {
        const newType: Reseller['settings']['orders']['colliTypes'][number] = {
            name: '',
            id: guid.generate()
        }

        this.setState({selectedOwnColliType: newType})
    }

    async removeColliType(event: MouseEvent, type: Reseller['settings']['orders']['colliTypes'][number]) {
        event.stopPropagation()

        if (!await this.popupRef.open('Bevestig verwijderen', `Weet je zeker dat je het collo type "${type.name}" wilt verwijderen?`)) return
        this.popupRef.close()

        const {reseller} = this.props
        reseller.settings.orders.colliTypes = reseller.settings.orders.colliTypes?.filter((t) => t.id !== type.id)

        this.props.onChange(reseller, 'orders.colliTypes')
    }

    render() {
        const {reseller} = this.props

        return (
            <Card shadow style={{width: 600, padding: 24, height: 'fit-content'}}>

                <H3 style={{marginBottom: 36}}>Opties</H3>

                <H5 style={{marginBottom: 24}}>Algemeen</H5>

                {!reseller.isShop &&
                    <>
                        <FormStack>
                            <Toggle
                                label='Scannen op hub'
                                info='Bezorgingen aangemeld door klanten krijgen de status Verwacht. Zodra deze op de hub arriveren moeten ze gescand worden voordat ze bezorgd kunnen worden.'
                                checked={reseller.settings.useExpectedOrders}
                                onChange={this.onChangeChecked.bind(this, 'useExpectedOrders')}
                            />

                            <FormRow>
                                <Select
                                    label='Toon melding bij nieuwe rit'
                                    options={[
                                        {
                                            value: 'never',
                                            title: 'Nooit'
                                        },
                                        {
                                            value: 'onlyToday',
                                            title: 'Alleen rit voor vandaag'
                                        },
                                        {
                                            value: 'always',
                                            title: 'Altijd'
                                        }
                                    ]}
                                    value={reseller.settings.orders.showNewOrderNotification}
                                    onChange={this.onSaveValue.bind(this, 'orders.showNewOrderNotification')}
                                    info='Om notificaties te kunnen tonen moeten deze zijn toegestaan in de instellingen van je besturingssysteem. Notificaties worden niet getoond voor ritten die je zelf aanmaakt.'
                                />

                                <Button
                                    variant='outline'
                                    onClick={() => {
                                        showNotification({orderId: 'Test melding', customerName: '[Klantnaam]'})
                                    }}
                                >
                                    Test notificatie
                                </Button>
                            </FormRow>
                        </FormStack>

                        <H5 style={{marginBottom: 24, marginTop: 36}}>Rit opties</H5>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <Select
                                label='Standaard datum voor nieuwe rit'
                                info="Kies hier of nieuwe ritten standaard op 'vandaag' in de planning komen, of op de datum die op dat moment zichtbaar is in de planning."
                                options={[
                                    {
                                        value: 'today',
                                        title: 'Vandaag'
                                    },
                                    {
                                        value: 'selectedDate',
                                        title: 'Geselecteerde datum in Planning'
                                    }
                                ]}
                                value={reseller.settings.orders.defaultDate}
                                onChange={this.onSaveValue.bind(this, 'orders.defaultDate')}
                            />

                            <Select
                                label='Adressen bij nieuwe rit'
                                info='Kies hier welke adressen standaard zichtbaar zijn bij het aanmaken van een nieuwe rit.'
                                options={[
                                    {
                                        value: 'deliver',
                                        title: 'Alleen bezorgadres'
                                    },
                                    {
                                        value: 'pickup',
                                        title: 'Alleen ophaaladres'
                                    },
                                    {
                                        value: 'pickupAndDeliver',
                                        title: 'Ophaal- en bezorgadres'
                                    }
                                ]}
                                value={reseller.settings.orders.defaultAddresses}
                                onChange={this.onSaveValue.bind(this, 'orders.defaultAddresses')}
                            />

                            <Select
                                label='Standaard opties ophaaladres'
                                options={[
                                    ...Object.entries(dbCodes.pickupOptions).map(([key, value]) => ({
                                        value: key,
                                        title: value
                                    })),
                                    ...(reseller.settings.orders.customOptions || []).map((option) => ({
                                        value: option.id,
                                        title: option.name,
                                        icon: 'mdi mdi-circle',
                                        iconColor: option.color,
                                        showIconInValue: true
                                    }))
                                ]}
                                values={reseller.settings.orders.defaultPickupOptions || []}
                                onChange={(event) => {
                                    this.onSaveValue('orders.defaultPickupOptions', undefined, {target: {value: event.target.values}})
                                }}
                            />

                            <Select
                                label='Standaard opties bezorgadres'
                                options={[
                                    ...Object.entries(dbCodes.pickupAndDeliveryOptions).map(([key, value]) => ({
                                        value: key,
                                        title: value
                                    })),
                                    ...(reseller.settings.orders.customOptions || []).map((option) => ({
                                        value: option.id,
                                        title: option.name,
                                        icon: 'mdi mdi-circle',
                                        iconColor: option.color,
                                        showIconInValue: true
                                    }))
                                ]}
                                values={reseller.settings.orders.defaultDeliveryOptions || []}
                                onChange={(event) => {
                                    this.onSaveValue('orders.defaultDeliveryOptions', undefined, {target: {value: event.target.values}})
                                }}
                            />

                            <Toggle
                                label='Automatisch optie Brievenbus toevoegen'
                                info='Als de koerier een zending in de brievenbus bezorgt die niet als brievenbuszending was aangemaakt, kan automatisch de bijbehorende optie worden toegekend. Dit bepaalt of de zending bij facturatie als brievenbuspakket wordt gefactureerd'
                                checked={reseller.settings.orders.autoMailboxOption}
                                onChange={(event) => {
                                    this.onChangeChecked('orders.autoMailboxOption', event)
                                }}
                            />

                            <Select
                                label='Rit opties tonen in planning'
                                info='Kies hier of de opties ook bij alle adressen in de dagplanning zichtbaar zijn. Voor koeriers zijn opties altijd zichtbaar.'
                                value={reseller.settings.orders.showOptionsInPlanning || 'no'}
                                options={[
                                    {value: 'no', title: 'Nee'},
                                    {value: 'customOptionsOnly', title: 'Alleen eigen opties'},
                                    {value: 'yes', title: 'Alle opties tonen'}
                                ]}
                                onChange={(event) => {
                                    reseller.settings.orders.showOptionsInPlanning = event.target.value as Reseller['settings']['orders']['showOptionsInPlanning']
                                    this.props.onChange(reseller, 'orders.showOptionsInPlanning')
                                }}
                            />
                        </FormStack>

                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <P>Eigen opties:</P>
                            <Info style={{marginLeft: 6}} text='Maak hier zelf opties aan die bij adressen gebruikt kunnen worden. Deze worden zichtbaar in de planning en voor koeriers.' color={Colors.textDark} />
                        </div>
                        {reseller.settings.orders.customOptions?.length > 0 && (
                            <Panel style={{marginTop: 12, flex: 1, overflowY: 'auto'}}>
                                <SortableList
                                    items={reseller.settings.orders.customOptions || []}
                                    onChange={(options: Reseller['settings']['orders']['customOptions']) => {
                                        reseller.settings.orders.customOptions = options
                                        this.props.onChange(reseller, 'orders.customOptions')
                                    }}
                                    renderItem={(option: Reseller['settings']['orders']['customOptions'][number]) => {
                                        return (
                                            <ListItem
                                                key={option.id}
                                                icon='mdi mdi-drag-horizontal'
                                                active={option.id === this.state.selectedOwnOption?.id}
                                                onClick={() => {
                                                    this.setState({selectedOwnOption: option})
                                                }}
                                            >
                                                <Icon
                                                    style={{marginRight: 6}}
                                                    icon='mdi mdi-circle'
                                                    color={option.color}
                                                />
                                                {option.name}

                                                <div style={{flex: 1}} />

                                                <IconButton
                                                    style={{marginLeft: 6}}
                                                    icon='mdi mdi-delete'
                                                    onClick={(event) => this.removeOwnOption(event, option)}
                                                />
                                            </ListItem>
                                        )
                                    }}
                                />
                            </Panel>
                        )}

                        {this.state.selectedOwnOption && (
                            <OwnOptionModal
                                onClose={() => this.setState({selectedOwnOption: null})}
                                onSave={(option) => {
                                    const index = reseller.settings.orders.customOptions?.findIndex((o) => o.id === option.id)
                                    if (index !== -1) {
                                        reseller.settings.orders.customOptions[index] = option
                                    } else {
                                        reseller.settings.orders.customOptions?.push(option)
                                    }
                                    this.setState({selectedOwnOption: null})

                                    this.props.onChange(reseller, 'orders.customOptions')
                                }}
                                option={this.state.selectedOwnOption}
                            />
                        )}

                        <Button
                            style={{marginTop: 24}}
                            variant='outline'
                            icon='mdi mdi-plus'
                            label={t('addOption')}
                            onClick={this.addCustomOptions.bind(this)}
                        />

                        <H5 style={{marginBottom: 24, marginTop: 36}}>Collo typen</H5>
                        <Select
                            label='Standaard collo typen'
                            info='Kies hier welke collo typen standaard beschikbaar zijn voor alle klanten'
                            options={(reseller.settings.orders.colliTypes || []).map(({name, id}) => ({
                                value: id,
                                title: name
                            }))}
                            values={(reseller.settings.orders.defaultColliTypes || [])}
                            onChange={(event) => {
                                this.onSaveValue('orders.defaultColliTypes', undefined, {target: {value: event.target.values}})
                            }}
                        />
                        {reseller.settings.orders.colliTypes?.length > 0 && (
                            <Panel style={{marginTop: 12, flex: 1, overflowY: 'auto'}}>
                                <SortableList
                                    items={reseller.settings.orders.colliTypes || []}
                                    onChange={(types: Reseller['settings']['orders']['colliTypes']) => {
                                        reseller.settings.orders.colliTypes = types
                                        this.props.onChange(reseller, 'orders.colliTypes')
                                    }}
                                    renderItem={(type: Reseller['settings']['orders']['colliTypes'][number]) => {
                                        return (
                                            <ListItem
                                                key={type.id}
                                                icon='mdi mdi-drag-horizontal'
                                                active={type.id === this.state.selectedOwnColliType?.id}
                                                onClick={() => {
                                                    this.setState({selectedOwnColliType: type})
                                                }}
                                            >
                                                {type.name}

                                                <div style={{flex: 1}} />

                                                <IconButton
                                                    style={{marginLeft: 6}}
                                                    icon='mdi mdi-delete'
                                                    onClick={(event) => this.removeColliType(event, type)}
                                                />
                                            </ListItem>
                                        )
                                    }}
                                />
                            </Panel>
                        )}

                        {this.state.selectedOwnColliType && (
                            <OwnOptionModal
                                onClose={() => this.setState({selectedOwnColliType: null})}
                                onSave={(type) => {
                                    const index = reseller.settings.orders.colliTypes?.findIndex((t) => t.id === type.id)
                                    if (index !== -1) {
                                        reseller.settings.orders.colliTypes[index] = type
                                    } else {
                                        reseller.settings.orders.colliTypes?.push(type)
                                    }
                                    this.setState({selectedOwnColliType: null})

                                    this.props.onChange(reseller, 'orders.colliTypes')
                                }}
                                option={this.state.selectedOwnColliType}
                            />
                        )}

                        <Button
                            style={{marginTop: 24}}
                            variant='outline'
                            icon='mdi mdi-plus'
                            label='Collo type toevoegen'
                            onClick={this.addColliTypes.bind(this)}
                        />

                        <H5 style={{marginBottom: 24, marginTop: 36}}>Ophalen niet mogelijk redenen</H5>
                        <P style={{marginBottom: 12}}>
                            Kies hieronder welke redenen er gekozen kunnen worden wanneer het ophalen niet mogelijk is.
                            Daarnaast kunnen specificaties worden aangemaakt die gekozen kunnen worden als toelichting bij een reden.
                        </P>

                        <ReasonsSettings
                            reseller={reseller}
                            customer={null}
                            onChange={this.props.onChange}
                            type='pickup'
                        />

                        <H5 style={{marginBottom: 24, marginTop: 36}}>Bezorgen niet mogelijk redenen</H5>
                        <P style={{marginBottom: 12}}>
                            Kies hieronder welke redenen er gekozen kunnen worden wanneer het bezorgen niet mogelijk is.
                            Daarnaast kunnen specificaties worden aangemaakt die gekozen kunnen worden als toelichting bij een reden.
                        </P>

                        <ReasonsSettings
                            reseller={reseller}
                            customer={null}
                            onChange={this.props.onChange}
                            type='delivery'
                        />

                        <H5 style={{marginBottom: 24, marginTop: 36}}>Koerier app</H5>

                        <FormStack>
                            <Toggle
                                label='Route inladen verplicht voor starten van route'
                                checked={reseller.settings.loadingRequiredBeforeStart}
                                onChange={this.onChangeChecked.bind(this, 'loadingRequiredBeforeStart')}
                            />

                            <Select
                                label={t('deliveryRadiusWarning')}
                                info='Bepaalt wat er gebeurt als de koerier zich bij het bezorgen niet binnen de hieronder ingevulde afstand van het bezorgadres bevindt.'
                                value={reseller.settings.deliveryRadiusWarning || ''}
                                onChange={this.onSaveValue.bind(this, 'deliveryRadiusWarning')}
                            >
                                <option value=''>{t('allowed')}</option>
                                <option value='warning'>{t('allowedWithWarning')}</option>
                                <option value='notallowed'>{t('notAllowed')}</option>
                            </Select>

                            <Input
                                type='number'
                                label='Maximale afwijking bezorgadres (meters)'
                                min={0}
                                value={reseller.settings.maxDeliveryRadius}
                                onBlur={(event) => this.onSaveValue('maxDeliveryRadius', parseInt, event)}
                                disabled={!reseller.settings.deliveryRadiusWarning}
                            />

                            <Select
                                label='Afwijken van route volgorde'
                                info=''
                                value={reseller.settings.nextStopWarning}
                                onChange={this.onSaveValue.bind(this, 'nextStopWarning')}
                            >
                                <option value=''>Toestaan</option>
                                <option value='notallowedanchored'>Niet toestaan voor stops in vaste zone van route</option>
                                <option value='notallowed'>Niet toestaan</option>
                            </Select>

                            <Toggle
                                label='Toon waarschuwing bij afwijken van route volgorde'
                                checked={reseller.settings.nextStopShowWarning}
                                disabled={reseller.settings.nextStopWarning === 'notallowed'}
                                onChange={this.onChangeChecked.bind(this, 'nextStopShowWarning')}
                            />

                            <Select
                                label='Starten van niet-geoptimaliseerde route'
                                info='Een niet-geoptimaliseerde route bevat adressen zonder stopnummer. Als het niet is toegestaan om deze route te starten, kan de koerier de route alleen starten door deze zelf te optimaliseren. Hierbij worden alle niet-vergrendelde stops geoptimaliseerd.'
                                value={reseller.settings.routeNotOptimizedWarning}
                                onChange={this.onSaveValue.bind(this, 'routeNotOptimizedWarning')}
                            >
                                <option value=''>Toestaan</option>
                                <option value='warning'>Toestaan met waarschuwing</option>
                                <option value='notallowed'>Niet toestaan</option>
                            </Select>

                            <Select
                                label={t('messengerEditCompletedAddress')}
                                info={t('messengerEditCompletedAddressInfo')}
                                value={reseller.settings.messengerEditCompletedAddress || ''}
                                onChange={this.onSaveValue.bind(this, 'messengerEditCompletedAddress')}
                            >
                                <option value=''>{t('no')}</option>
                                <option value='last'>{t('onlyLastCompletedAddress')}</option>
                                <option value='route'>{t('allAddressesInActiveRoute')}</option>
                            </Select>

                            <Input
                                type='number'
                                label={t('messengerEditCompletedAddressMinutes')}
                                info={t('messengerEditCompletedAddressMinutesInfo')}
                                min={0}
                                value={reseller.settings.messengerEditCompletedAddressMinutes ?? ''}
                                onBlur={(event) => this.onSaveValue('messengerEditCompletedAddressMinutes', (value) => parseInt(value) || null, event)}
                                disabled={!reseller.settings.messengerEditCompletedAddress}
                            />
                        </FormStack>
                    </>
                }

                <H5 style={{marginBottom: 24, marginTop: 36}}>Routeplanning</H5>

                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <Select
                        label='Standaard voertuig'
                        value={reseller.settings.defaultVehicle}
                        onChange={this.onSaveValue.bind(this, 'defaultVehicle')}
                        options={reseller.settings.vehicleTypes.filter((v) => !v.isArchived).map(({_id, name, icon}) => ({value: _id, title: name, icon: `mdi mdi-${icon}`}))}
                    />

                    {!reseller.permissions.vehicles &&
                        <Input
                            type='number'
                            label='Standaard stoptijd (seconden)'
                            placeholder={180}
                            value={reseller.settings.stopTime}
                            onChange={this.onChangeValue.bind(this, 'stopTime', parseInt)}
                            onBlur={this.onSaveValue.bind(this, 'stopTime', parseInt)}
                        />
                    }

                    <Toggle
                        label='Optimaliseer voor tijdvakken'
                        info='Als deze optie is aangevinkt houdt de routeplanner rekening met de tijdvakken van adressen. Deze instelling kan op route niveau worden overschreven.'
                        checked={reseller.settings.optimizeForTimeslots}
                        onChange={this.onChangeChecked.bind(this, 'optimizeForTimeslots')}
                    />

                    <Select
                        label='Route aanpasbaar door koerier'
                        info='Als deze optie is aangevinkt dan kan de koerier de route aanpassen in de koerier app. Deze instelling kan op route niveau worden overschreven.'
                        value={reseller.settings.adjustableByMessengerGrouped ? 'yes' : 'no'}
                        onChange={(event) => {
                            event.target.checked = event.target.value === 'yes'
                            this.onChangeChecked('adjustableByMessengerGrouped', event)
                        }}
                    >
                        <option value='yes'>Ja</option>
                        <option value='no'>Nee</option>
                    </Select>

                    {reseller.settings.adjustableByMessengerGrouped &&
                        <FormStack style={{marginLeft: formSpacing.section, width: 'auto'}}>
                            <Toggle
                                label='Route volgorde wijzigen'
                                info='Als deze optie is aangevinkt dan kan de koerier de route volgorde aanpassen in de koerier app. Deze instelling kan op route niveau worden overschreven.'
                                checked={reseller.settings.adjustableByMessenger}
                                onChange={this.onChangeChecked.bind(this, 'adjustableByMessenger')}
                            />

                            <Toggle
                                label='Nieuw adres aanmaken'
                                info='Als deze optie is aangevinkt dan kan de koerier een nieuw adres toevoegen in de koerier app. Deze instelling kan op route niveau worden overschreven.'
                                checked={reseller.settings.allowAddAddress}
                                onChange={this.onChangeChecked.bind(this, 'allowAddAddress')}
                            />

                            <Toggle
                                label='Eigen opties wijzigen'
                                info='Kies hier of de koerier zelf de eigen aangemaakte opties mag wijzigen bij adressen.'
                                checked={reseller.settings.orders.allowMessengerEditCustomOptions}
                                onChange={this.onChangeChecked.bind(this, 'orders.allowMessengerEditCustomOptions')}
                            />

                            <Select
                                label='Collo toevoegen toestaan'
                                info='Mag de Koerier colli toevoegen bij het adres. Deze instelling kan op klant- en ritniveau worden overschreven.'
                                options={[
                                    {title: 'Ja', value: 'yes'},
                                    {title: 'Alleen bij bezorgen', value: 'deliver'},
                                    {title: 'Alleen bij ophalen', value: 'pickup'},
                                    {title: 'Nee', value: ''}
                                ]}
                                value={reseller.settings.allowAddCollo}
                                onChange={this.onChangeValue.bind(this, 'allowAddCollo')}
                                onBlur={this.onSaveValue.bind(this, 'allowAddCollo')}
                            />

                            <Select
                                label='Bestaande rit toevoegen aan route'
                                info='Kies hier of koeriers zelf bekende ritten kunnen toevoegen aan hun route. Een ongeplande rit betekend een rit die niet in een route staat.'
                                value={reseller.settings.messengerAddOrderWarning}
                                onChange={this.onSaveValue.bind(this, 'messengerAddOrderWarning')}
                            >
                                <option value='notallowed'>Niet toestaan</option>
                                <option value='allowed'>Toestaan alle ritten</option>
                                <option value='only_today'>Toestaan ritten op vandaag</option>
                                <option value='only_unplanned'>Toestaan ongeplande ritten</option>
                                <option value='only_unplanned_today'>Toestaan voor ongeplande ritten op vandaag</option>
                            </Select>

                            <Toggle
                                label='Toon waarschuwing bij toevoegen bestaande rit'
                                checked={reseller.settings.showWarningOnAddExistingOrder}
                                onChange={this.onChangeChecked.bind(this, 'showWarningOnAddExistingOrder')}
                            />
                        </FormStack>
                    }

                    <Toggle
                        label='Route automatisch afronden na laatste stop'
                        info='Als deze optie is aangevinkt dan wordt de route automatisch afgerond nadat de laatste stop is bereikt.'
                        checked={reseller.settings.orders.autoCompleteRoutes}
                        onChange={this.onChangeChecked.bind(this, 'orders.autoCompleteRoutes')}
                    />
                </FormStack>

                <Popup
                    ref={(ref) => {this.popupRef = ref}}
                />
            </Card>
        )
    }
}
